/**
 * 移动端邮件营销管理页
 */
import React, { useEffect, useState } from 'react';
import {
  List,
  PullToRefresh,
  Empty,
  Button,
  Dialog,
  Form,
  Input,
  Picker,
  Tag,
  Radio,
  Skeleton,
} from 'antd-mobile';
import { adminEmailCampaignApi, adminCustomerSegmentApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';
import { useNavigate } from 'react-router-dom';

// 状态配置
const STATUS_CONFIG = {
  draft: { color: 'default', label: '草稿' },
  scheduled: { color: 'blue', label: '已计划' },
  sending: { color: 'processing', label: '发送中' },
  completed: { color: 'success', label: '已完成' },
  failed: { color: 'error', label: '失败' },
};

export default function MobileEmailCampaignListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [sendingModalVisible, setSendingModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [sendingCampaign, setSendingCampaign] = useState(null);
  const [form] = Form.useForm();
  const [recipientType, setRecipientType] = useState('segment');
  const [sending, setSending] = useState(false);
  // 预览和发送测试
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmailModalVisible, setTestEmailModalVisible] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [previewCampaign, setPreviewCampaign] = useState(null);

  useEffect(() => {
    fetchCampaigns();
    fetchSegments();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const data = await adminEmailCampaignApi.list();
      console.log('获取营销活动列表:', data);
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('获取营销活动列表失败:', err);
      Toast.show('获取营销活动列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchSegments = async () => {
    try {
      const data = await adminCustomerSegmentApi.list();
      setSegments(data || []);
    } catch (err) {
      console.error('加载客户细分列表失败', err);
    }
  };

  const refreshData = async () => {
    await fetchCampaigns();
  };

  const openForm = (campaign) => {
    setEditingCampaign(campaign);
    if (campaign) {
      setRecipientType(campaign.recipientType || 'segment');
      form.setFieldsValue({
        name: campaign.name,
        subject: campaign.subject,
        fromName: campaign.fromName,
        fromEmail: campaign.fromEmail,
        recipientType: campaign.recipientType,
        segmentId: campaign.segmentId,
      });
    } else {
      setRecipientType('segment');
      form.resetFields();
    }
    setFormModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      console.log('提交数据:', { ...values, recipientType });
      const data = {
        ...values,
        recipientType,
      };

      if (editingCampaign) {
        await adminEmailCampaignApi.update(editingCampaign.id, data);
        Toast.show('更新成功');
      } else {
        const result = await adminEmailCampaignApi.create(data);
        console.log('创建结果:', result);
        Toast.show('创建成功');
      }
      setFormModalVisible(false);
      await fetchCampaigns();
    } catch (err) {
      console.error('提交失败:', err);
      Toast.show(err?.response?.data?.message || '操作失败');
    }
  };

  const handleDelete = (id) => {
    Dialog.confirm({
      content: '确定要删除此营销活动吗？',
      onConfirm: async () => {
        try {
          await adminEmailCampaignApi.delete(id);
          Toast.show('删除成功');
          fetchCampaigns();
        } catch (err) {
          Toast.show(err?.response?.data?.message || '删除失败');
        }
      },
    });
  };

  const handleSend = async (campaign) => {
    try {
      const recipients = await adminEmailCampaignApi.getRecipients(campaign.id);
      const updatedCampaign = {
        ...campaign,
        totalRecipients: Array.isArray(recipients) ? recipients.length : 0,
      };
      setSendingCampaign(updatedCampaign);
      setSendingModalVisible(true);
    } catch (err) {
      Toast.show('获取收件人列表失败');
    }
  };

  const confirmSend = async () => {
    if (!sendingCampaign) return;
    setSending(true);
    try {
      await adminEmailCampaignApi.send(sendingCampaign.id);
      Toast.show('邮件发送任务已启动');
      setSendingModalVisible(false);
      fetchCampaigns();
    } catch (err) {
      Toast.show('发送失败');
    } finally {
      setSending(false);
    }
  };

  const handleViewDetail = (campaign) => {
    navigate(`/admin/m/email-campaigns/${campaign.id}`);
  };

  // 预览邮件
  const handlePreview = async (campaign) => {
    setPreviewCampaign(campaign);
    setPreviewLoading(true);
    setPreviewModalVisible(true);
    try {
      const data = await adminEmailCampaignApi.preview(campaign.id);
      setPreviewData(data);
    } catch (err) {
      Toast.show(err?.response?.data?.message || '预览失败');
      setPreviewModalVisible(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // 发送测试邮件
  const handleTestEmail = (campaign) => {
    setPreviewCampaign(campaign);
    setTestEmail('');
    setTestEmailModalVisible(true);
  };

  const confirmTestEmail = async () => {
    if (!testEmail || !testEmail.trim()) {
      Toast.show('请输入邮箱地址');
      return;
    }
    setTestEmailLoading(true);
    try {
      await adminEmailCampaignApi.sendTest(previewCampaign.id, testEmail);
      Toast.show('测试邮件已发送');
      setTestEmailModalVisible(false);
    } catch (err) {
      Toast.show(err?.response?.data?.message || '发送失败');
    } finally {
      setTestEmailLoading(false);
    }
  };

  // 复制营销活动
  const handleCopy = async (campaign) => {
    try {
      await adminEmailCampaignApi.copy(campaign.id);
      Toast.show('活动已复制');
      fetchCampaigns();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '复制失败');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton.Title rows={3} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>邮件营销</h2>
        <Button size="small" color="primary" onClick={() => openForm(null)}>
          新建
        </Button>
      </div>

      <PullToRefresh onRefresh={refreshData}>
        {campaigns.length > 0 ? (
          <List>
            {campaigns.map((campaign) => {
              const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
              return (
                <List.Item
                  key={campaign.id}
                  style={{
                    background: '#fff',
                    marginBottom: 12,
                    padding: '12px 16px',
                  }}
                  description={
                    <div style={{ fontSize: 13, color: '#999', marginTop: 4 }}>
                      {campaign.subject} | 总数：{campaign.totalRecipients || 0} | 成功：{campaign.successCount || 0}
                    </div>
                  }
                  extra={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <span
                        style={{ fontSize: 13, color: '#faad14', cursor: 'pointer' }}
                        onClick={() => handlePreview(campaign)}
                      >
                        预览
                      </span>
                      <span
                        style={{ fontSize: 13, color: '#722ed1', cursor: 'pointer' }}
                        onClick={() => handleTestEmail(campaign)}
                      >
                        测试
                      </span>
                      {campaign.status === 'draft' && (
                        <span
                          style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer' }}
                          onClick={() => handleSend(campaign)}
                        >
                          发送
                        </span>
                      )}
                      <span
                        style={{ fontSize: 13, color: '#52c41a', cursor: 'pointer' }}
                        onClick={() => handleViewDetail(campaign)}
                      >
                        详情
                      </span>
                      <span
                        style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer' }}
                        onClick={() => openForm(campaign)}
                      >
                        编辑
                      </span>
                      <span
                        style={{ fontSize: 13, color: '#ff4d4f', cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(campaign.id);
                        }}
                      >
                        删除
                      </span>
                    </div>
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 500 }}>{campaign.name}</div>
                    <Tag color={statusConfig.color} size="small">
                      {statusConfig.label}
                    </Tag>
                  </div>
                </List.Item>
              );
            })}
          </List>
        ) : (
          <div style={{ padding: '80px 0' }}>
            <Empty description="暂无营销活动" />
          </div>
        )}
      </PullToRefresh>

      {/* 新建/编辑弹窗 */}
      <Dialog
        visible={formModalVisible}
        style={{ '--content-width': '90%', '--content-max-height': '90%' }}
        content={
          <div style={{ padding: '16px 0', maxHeight: '70vh', overflow: 'auto' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              {editingCampaign ? '编辑营销活动' : '新建营销活动'}
            </h3>
            <Form
              form={form}
              layout="vertical"
              footer={
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <Button block type="submit" color="primary" onClick={() => form.submit()}>
                    保存
                  </Button>
                  <Button block variant="outline" onClick={() => setFormModalVisible(false)}>
                    取消
                  </Button>
                </div>
              }
              onFinish={handleSubmit}
            >
              <Form.Item name="name" label="活动名称" rules={[{ required: true }]}>
                <Input placeholder="例：春季促销活动" />
              </Form.Item>
              <Form.Item name="subject" label="邮件主题" rules={[{ required: true }]}>
                <Input placeholder="例：春季特惠，全场 8 折起！" />
              </Form.Item>
              <Form.Item name="fromName" label="发件人名称">
                <Input placeholder="例：YK2 商店" />
              </Form.Item>
              <Form.Item name="fromEmail" label="发件人邮箱">
                <Input placeholder="例：noreply@yk2store.com" />
              </Form.Item>

              <Form.Item name="recipientType" label="收件人类型" initialValue="segment">
                <Radio.Group
                  value={recipientType}
                  onChange={(val) => {
                    setRecipientType(val);
                    form.setFieldsValue({ recipientType: val });
                  }}
                >
                  <Radio value="segment">按客户细分</Radio>
                  <Radio value="all">全部客户</Radio>
                  <Radio value="manual">手动指定</Radio>
                </Radio.Group>
              </Form.Item>

              {recipientType === 'segment' && (
                <Form.Item name="segmentId" label="选择客户细分">
                  <Picker
                    options={segments.map(seg => ({
                      label: `${seg.name} (${seg.customerCount || 0}人)`,
                      value: seg.id,
                    }))}
                    placeholder="请选择客户细分"
                  >
                    {(items) => <div>{items[0]?.label || '请选择客户细分'}</div>}
                  </Picker>
                </Form.Item>
              )}

              {recipientType === 'manual' && (
                <Form.Item name="customerIds" label="客户 ID 列表（JSON）">
                  <Input placeholder='例：[1, 2, 3]' />
                </Form.Item>
              )}
            </Form>
          </div>
        }
        closeOnMaskClick={false}
      />

      {/* 发送确认弹窗 */}
      <Dialog
        visible={sendingModalVisible}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>确认发送邮件</h3>
            <p style={{ fontSize: 14, marginBottom: 16 }}>
              确认要发送邮件给 <strong>{sendingCampaign?.totalRecipients || 0}</strong> 位收件人吗？
            </p>
            <p style={{ fontSize: 13, color: '#faad14' }}>
              注意：邮件发送可能需要较长时间，请耐心等待。
            </p>
          </div>
        }
        actions={[
          {
            key: 'cancel',
            text: '取消',
            onClick: () => setSendingModalVisible(false),
          },
          {
            key: 'confirm',
            text: '确认发送',
            color: 'primary',
            loading: sending,
            onClick: confirmSend,
          },
        ]}
      />

      {/* 预览邮件弹窗 */}
      <Dialog
        visible={previewModalVisible}
        style={{ '--content-width': '95%' }}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>预览邮件</h3>
            {previewLoading ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                加载中...
              </div>
            ) : previewData ? (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontWeight: 500 }}>主题：</span>
                  <span>{previewData.subject}</span>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontWeight: 500 }}>示例收件人：</span>
                  <span>{previewData.sampleRecipient}</span>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontWeight: 500 }}>收件人数量：</span>
                  <span>{previewData.recipientCount} 人</span>
                </div>
                <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500 }}>邮件内容：</div>
                <div
                  style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    padding: 12,
                    maxHeight: 300,
                    overflow: 'auto',
                    background: '#fafafa',
                    fontSize: 13,
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: previewData.content }} />
                </div>
              </div>
            ) : null}
          </div>
        }
        actions={[
          {
            key: 'close',
            text: '关闭',
            onClick: () => setPreviewModalVisible(false),
          },
        ]}
      />

      {/* 发送测试邮件弹窗 */}
      <Dialog
        visible={testEmailModalVisible}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>发送测试邮件</h3>
            <p style={{ fontSize: 14, marginBottom: 12 }}>输入测试邮箱地址：</p>
            <Input
              placeholder="请输入邮箱地址"
              value={testEmail}
              onChange={setTestEmail}
              style={{ marginBottom: 12 }}
            />
            <p style={{ fontSize: 12, color: '#999' }}>
              测试邮件会在主题前添加 [测试] 标识
            </p>
          </div>
        }
        actions={[
          {
            key: 'cancel',
            text: '取消',
            onClick: () => setTestEmailModalVisible(false),
          },
          {
            key: 'send',
            text: '发送',
            color: 'primary',
            loading: testEmailLoading,
            onClick: confirmTestEmail,
          },
        ]}
      />
    </div>
  );
}
